import React from 'react';
import {
  Alert,
  Box, Card, CardContent, CircularProgress, Grid, Snackbar, Theme, Typography, useTheme,
} from '@mui/material';
import { lightGreen } from '@mui/material/colors';
import { useForm, UseFormGetValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CallToActionButton from '../../../../common/CallToActionButton';
import { IFormInputs } from '../../../../common/StyledFormInputs';
import { getFullAddressUS, sanitizeString } from '../../../../common/utils/formUtils';
import { Member } from '../../../../common/models/Member';

interface EnrollmentCardHeaderProps {
  children: JSX.Element | null,
  title: string,
  subtitle?: string | null,
  logo: string,
  imageWidth: number,
}

interface CTAPromptCardProps {
  description: string,
  btnText: string,
  dataTestId: string,
  optionalBtnStyles?: {
    [k: string]: unknown;
  },
  onBtnClick?: () => void,
}

interface DoneCardProps {
  message: string,
}

interface DialogHeaderProps {
  SolutionOrgLogo?: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | string | null,
  partnerLogo: string,
  formSubmitted?: boolean,
  heading1: string,
  heading1b?: string,
  heading2: string
}

interface SnackbarComponentProps {
  snackbarOpen: boolean,
  errorMessage: string,
  handleSnackbarClose?: () => void,
}

interface DialogCTAComponentProps {
  formSubmitted?: boolean,
  loading: boolean,
  submitBtnTestId: string,
  closeBtnTestId?: string,
  handleClick?: () => void,
  isProfileForm?: boolean,
  extraDisableCondition?: boolean,
  btnText?: string,
  noBgColor?: boolean,
}

export interface PartnerEnrollmentCardProps {
  // eslint-disable-next-line no-shadow
  optionalCardStyle?: (theme: Theme) => {
    [key: string]: unknown;
  },
  optionalBtnStyles?: {
    [k: string]: unknown;
  };
  updateOpenState?: (event: boolean) => void;
  updateConfirmationState?: (event: boolean) => void;
  confirmationState?: boolean;
}

const buttonStyle = (theme: Theme) => ({
  position: 'static',
  width: '100%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  background: theme.palette.brand.primary,
  borderRadius: theme.spacing(3.75),
  fontWeight: '600',
  fontSize: theme.spacing(2.3),
  '&:hover': {
    backgroundColor: theme.palette.brand.primary,
  },
  '&:disabled': {
    backgroundColor: theme.palette.brand.primary,
    opacity: '0.4',
    color: theme.palette.common.white,
  },
  [theme.breakpoints.up('sm')]: {
    width: '100%',
    marginLeft: theme.spacing(0),
  },
  [theme.breakpoints.between('sm', 'md')]: {
    width: '60% !important',
    marginLeft: theme.spacing(0),
  },
} as const);

export const optionalCardStyle = (theme: Theme) => ({
  width: '100%',
  marginTop: theme.spacing(0),
  marginBottom: theme.spacing(0),
  height: 'fit-content',
  border: theme.spacing(0),
  p: theme.spacing(2),
} as const);

export const cardStyle = (theme: Theme) => ({
  background: theme.palette.common.white,
  paddingLeft: theme.spacing(3.5),
  paddingRight: theme.spacing(3.5),
  paddingTop: theme.spacing(3),
  marginTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderRadius: theme.spacing(1.6),
  border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
} as const);

export const EnrollmentCardHeader = ({
  children, title, subtitle, logo, imageWidth,
}: EnrollmentCardHeaderProps) => {
  const theme = useTheme();
  return (
    <Box display={'flex'} sx={{
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(3),
    }}>
      <Box display={'flex'} alignItems={'center'}>
        <img src={logo as string}
          style={{
            width: theme.spacing(imageWidth),
            height: 'auto',
          }}
          alt={title} />
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginTop: theme.spacing(0.5),
          marginBottom: theme.spacing(1.5),
          marginLeft: theme.spacing(2.5),
          marginRight: theme.spacing(2.5),
        }}>
          <Typography variant='h4' component='div' sx={{

            [theme.breakpoints.down('md')]: {
              fontSize: theme.spacing(2),
            },
          }}>
            {title}
          </Typography>
          {
            subtitle &&
          <Typography variant="body8" component="div">
            {subtitle}
          </Typography>
          }
        </Box>
        <Box sx={{ mb: theme.spacing(1) }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export const CTAPromptCard = ({
  description, btnText, optionalBtnStyles, dataTestId, onBtnClick,
}: CTAPromptCardProps) => {
  const theme = useTheme();
  return (
    <>
      <Typography
        sx={{
          color: theme.palette.grey[500],
          marginBottom: theme.spacing(1),
        }}>
        {description}
      </Typography>
      <Grid container
        sx={{
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Grid item xs={12} sm={12} md={4} lg={4}
          sx={{
            justifyContent: 'center !important',
            [theme.breakpoints.down('md')]: {
              justifyContent: 'center',
            },
            '& .MuiStack-root': {
              justifyContent: 'center',
            },
          }}
        >
          <CallToActionButton
            text={btnText}
            type={'submit'}
            onClick={onBtnClick}
            sx={[buttonStyle(theme), ...(optionalBtnStyles ? [optionalBtnStyles] : [])]}
            data-testid={dataTestId}
          />
        </Grid>
      </Grid>
    </>
  );
};

export const DoneCard = ({ message }: DoneCardProps) => (
  <Box sx={(theme) => ({
    color: theme.palette.grey[500],
  })}>
    <Typography component='span'>
      {message}
    </Typography>
  </Box>
);

export const DialogHeader = ({
  SolutionOrgLogo, partnerLogo, formSubmitted, heading1, heading2, heading1b,
}: DialogHeaderProps) => {
  const theme = useTheme();
  return (
    <>
      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              mb: theme.spacing(0),
              mt: theme.spacing(0),
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                alignItems: 'center',
                gap: theme.spacing(2),
              },
            }}
          >
            <Box
              sx={{
                height: theme.spacing(10),
                width: theme.spacing(10),
                p: theme.spacing(1.5),
                border: '1px solid #DDD',
                borderRadius: theme.spacing(1),
              }}
            >
              <Box sx={{
                width: theme.spacing(6.8),
                height: theme.spacing(6.8),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',

              }}>
                <img src={partnerLogo as string}
                  style={{
                    width: '100%',
                    height: 'auto',
                  }}
                  alt='partner logo' />
              </Box>
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              sx={{
                ml: theme.spacing(2),
              }}
            >
              {formSubmitted && <CheckCircleIcon sx={{
                marginRight: theme.spacing(1.25),
                width: theme.spacing(3),
                height: theme.spacing(3),
                color: lightGreen[800],
              }} />}
              <Typography variant="h2" component="div">
                {!formSubmitted ?
                  heading1 :
                  heading2
                }
              </Typography>
              {
                heading1b &&
              <Typography variant="body8" component="div">
                {heading1b}
              </Typography>
              }
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export function FormMethods(memberInfo: Member) {
  const methods = useForm<IFormInputs>({
    defaultValues: {
      memberInfo: {
        firstName: sanitizeString(memberInfo.firstName),
        lastName: sanitizeString(memberInfo.lastName),
        email: sanitizeString(memberInfo.email),
        phone: sanitizeString(memberInfo.phoneNumber),
      },
      businessInfo: {
        name: sanitizeString(memberInfo?.companyName),
        email: sanitizeString(memberInfo?.companyEmail),
        phone: sanitizeString(memberInfo?.companyPhoneNumber),
        website: sanitizeString(memberInfo?.companyWebsiteUrl),
        role: sanitizeString(memberInfo?.companyRoleId),
        autoAddress: {
          street: sanitizeString(memberInfo?.companyAddressStreet),
          city: sanitizeString(memberInfo.companyAddressCity),
          state: sanitizeString(memberInfo.companyAddressState),
          zipcode: sanitizeString(memberInfo.companyAddressZipCode),
          fullAddress: sanitizeString(memberInfo?.companyFullAddress),
          validAddress: getFullAddressUS(
            memberInfo?.companyAddressStreet,
            memberInfo.companyAddressCity,
            memberInfo.companyAddressState,
            memberInfo.companyAddressZipCode,
          ),
        },
      },
      companyConfig: {
        partnerEnrollmentQuestions: memberInfo.company?.partnerEnrollmentQuestions || {
          questions: {
            number_of_vehicles: {
              title: 'Number of vehicles',
              answer: '', // fallback if no data
            },
          },
        },
      },
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  return methods;
}

export const SnackbarComponent = ({
  snackbarOpen, errorMessage, handleSnackbarClose,
}: SnackbarComponentProps) => (
  <Snackbar
    open={snackbarOpen}
    autoHideDuration={4000}
    onClose={handleSnackbarClose}
    anchorOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
  >
    <Alert
      onClose={handleSnackbarClose}
      severity="error"
      sx={{ width: '100%' }}
    >
      {errorMessage}
    </Alert>
  </Snackbar>
);

export function DialogCTAComponent({
  formSubmitted, loading,
  submitBtnTestId, closeBtnTestId, handleClick,
  btnText,
  extraDisableCondition = false,
  noBgColor = false,
}: DialogCTAComponentProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  // Shared styles
  const noBgStyle = {
    backgroundColor: theme.palette.common.white,
    color: 'text.disabled',
    opacity: '1',
    border: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
    },
  };

  return (<Box
    width={'100%'}
    sx={{
      mt: theme.spacing(3),
      mb: theme.spacing(2),
    }}
  >
    <CallToActionButton
      text={
        loading ? (
          <CircularProgress color="inherit" size={24} />
        ) : (
          (btnText && btnText.trim()) ||
          (!formSubmitted ? t('button.submit') : t('button.close')) || ''
        )
      }
      type={!formSubmitted ? 'submit' : 'button'}
      sx={{
        position: 'static',
        width: '100% !important',
        height: 'fit-content',
        background: theme.palette.brand.primary,
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(0),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        fontWeight: '600',
        fontSize: `${theme.spacing(1.75)} !important`,
        '&:hover': {
          backgroundColor: theme.palette.brand.primary,
        },
        '&:disabled': noBgStyle,
        ...(noBgColor ? noBgStyle : {}),
        [theme.breakpoints.up('sm')]: {
          width: '100%',
          marginLeft: theme.spacing(0),
        },
      }}
      onBtnClick={() => handleClick?.()}
      disable={loading || extraDisableCondition}
      data-testid={!formSubmitted ?
        submitBtnTestId :
        closeBtnTestId
      }
    />
  </Box>);
}

export const updatedCompanyRequest = (getValues: UseFormGetValues<IFormInputs>) => (
  {
    name: getValues('businessInfo.name'),
    email: getValues('businessInfo.email'),
    phoneNumber: getValues('businessInfo.phone'),
    websiteUrl: getValues('businessInfo.website') || '',
    street: getValues('businessInfo.autoAddress.street') || '',
    city: getValues('businessInfo.autoAddress.city') || '',
    state: getValues('businessInfo.autoAddress.state') || '',
    zipCode: getValues('businessInfo.autoAddress.zipcode') || '',
    fullAddress: getValues('businessInfo.autoAddress.fullAddress'),
  }
);
